<template>
  <!-- Email Reminder Confirmation Dialog -->
  <lb-dialog 
    v-model="internalValue" 
    heading="Send Email Reminder" 
    :width="hasEligibleReportingUnits ? '800' : '400'"
    data-testid="email-reminder-dialog"
  >
    <!-- Dialog Body -->
    <template v-slot:body>
      <div class="" data-testid="email-reminder-dialog__body">
        <p class="subtitle-1 mb-4 noTransform text-left">
          You're about to send reminder emails for the selected reporting units 
          to the respective Local Preparers or Local Approvers, based on your 
          assigned role for each Reporting unit
        </p>
        
        <!-- Table showing eligible RUs -->
        <div 
          v-if="hasEligibleReportingUnits" 
          class="eligible-ru-table-wrapper mt-4"
          data-testid="email-reminder-dialog__table-wrapper"
        >
          <lb-datatablenew
            class="email-reminder-table"
            :headers="headers"
            :items="tableItems"
            :enableslot="['serial', 'l1Status', 'l2Status', 'userRole', 'reportingUnit']"
            :perpage="1000"
            :hide-default-footer="true"
            data-testid="email-reminder-dialog__table"
          >
            <template v-slot:serial="{ item }">
              <span data-testid="email-reminder-dialog__td-serial">{{ item.serial }}</span>
            </template>

            <template v-slot:l1Status="{ item }">
              <div data-testid="email-reminder-dialog__td-l1-status">
                <StatusIndicator
                  :variant="item.l1StatusVariant || getStatusVariant(item.l1Status, 'local')"
                  :custom-label="item.l1StatusLabel || '-'"
                  size="sm"
                />
              </div>
            </template>

            <template v-slot:l2Status="{ item }">
              <div data-testid="email-reminder-dialog__td-l2-status">
                <StatusIndicator
                  :variant="item.l2StatusVariant || getStatusVariant(item.l2Status, 'regional')"
                  size="sm"
                />
              </div>
            </template>

            <template v-slot:userRole="{ item }">
              <span data-testid="email-reminder-dialog__td-user-role">{{ item.userRole || '-' }}</span>
            </template>

            <template v-slot:reportingUnit="{ item }">
              <div data-testid="email-reminder-dialog__td-reporting-unit">
                <span class="font-weight-medium">{{ item.code }}</span> - {{ item.name }}
              </div>
            </template>
          </lb-datatablenew>
        </div>
      </div>
    </template>

    <!-- Dialog Actions -->
    <template v-slot:actions>
      <v-spacer></v-spacer>
      
      <!-- Cancel Button -->
      <v-btn 
        text 
        @click="handleCancel" 
        class="secondaryBtn"
        data-testid="email-reminder-dialog__cancel-btn"
      >
        <span>Cancel</span>
      </v-btn>
      
      <!-- Send Button -->
      <v-btn 
        color="primary" 
        @click="handleConfirm"
        :loading="loading"
        :disabled="!hasEligibleReportingUnits && eligibleReportingUnits !== null"
        data-testid="email-reminder-dialog__send-btn"
      >
        <span>Send</span>
      </v-btn>
    </template>
  </lb-dialog>
</template>

<script>
/**
 * EmailReminderDialog Component
 * 
 * A confirmation dialog for sending email reminders to Local Preparers
 * or Local Approvers for selected reporting units.
 * Displays a table of eligible reporting units with their status information.
 * 
 * @component
 * @example
 * <EmailReminderDialog
 *   v-model="showDialog"
 *   :eligible-reporting-units="eligibleRUs"
 *   :loading="isLoading"
 *   @confirm="handleSendReminder"
 *   @cancel="handleCancelReminder"
 * />
 */

import StatusIndicator from '../components/StatusIndicator.vue';
import { STATUS_VARIANTS } from '../utils/statusConstants';
import { getStatusVariantByCode } from '../utils/statusHelpers';

export default {
  name: 'EmailReminderDialog',

  components: {
    StatusIndicator,
  },

  props: {
    /**
     * Controls dialog visibility (v-model)
     * @type {Boolean}
     * @default false
     */
    value: {
      type: Boolean,
      default: false
    },
    /**
     * Loading state for the send button
     * @type {Boolean}
     * @default false
     */
    loading: {
      type: Boolean,
      default: false
    },
    /**
     * List of eligible reporting units to display in the table
     * Each item should have: cycleId, code, name, l1Status, l1StatusLabel, l2Status, l2StatusLabel, userRole
     * @type {Array}
     * @default []
     */
    eligibleReportingUnits: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      /**
       * Internal value for v-model support
       * Synced with parent component via value prop
       */
      internalValue: this.value,
      headers: [
        { text: 'S.No', value: 'serial', sortable: false, width: '60px', align: 'center' },
        { 
          text: `${this.$nova.labelsName('my_reporting_units_page', 'commentary_local_key_label', 'Local')} ${this.$nova.labelsName('my_reporting_units_page', 'commenrary_ru_label', 'RU')} Status`, 
          value: 'l1Status', 
          sortable: false, 
          align: 'left',
          width: '180px'
        },
        { 
          text: `${this.$nova.labelsName('my_reporting_units_page', 'commentary_regional_key_label', 'Regional')} ${this.$nova.labelsName('my_reporting_units_page', 'commenrary_ru_label', 'RU')} Status`, 
          value: 'l2Status', 
          sortable: false, 
          align: 'left',
          width: '200px' 
        },
        { text: 'User Role', value: 'userRole', sortable: false, align: 'left', width: '180px' },
        { text: 'Reporting Unit', value: 'reportingUnit', sortable: false, align: 'left' }
      ],
    };
  },

  computed: {
    /**
     * Check if there are eligible reporting units to display
     * @returns {Boolean}
     */
    hasEligibleReportingUnits() {
      return this.eligibleReportingUnits && this.eligibleReportingUnits.length > 0;
    },
    /**
     * Prepare items for the table with serial number
     */
    tableItems() {
      return this.eligibleReportingUnits.map((item, index) => ({
        ...item,
        serial: index + 1,
        reportingUnit: item, // Pass full item to slot if needed, or just use item in slot
      }));
    }
  },

  watch: {
    /**
     * Watch for external value changes
     * Updates internal state when parent changes value
     */
    value(newVal) {
      this.internalValue = newVal;
    },

    /**
     * Watch for internal value changes
     * Emits input event to update parent v-model
     */
    internalValue(newVal) {
      this.$emit('input', newVal);
    }
  },

  methods: {
    /**
     * Handle cancel button click
     * Closes dialog and emits cancel event
     * @emits cancel
     */
    handleCancel() {
      this.internalValue = false;
      this.$emit('cancel');
    },

    /**
     * Handle confirm button click
     * Emits confirm event for sending emails
     * Dialog remains open until parent closes it
     * @emits confirm
     */
    handleConfirm() {
      this.$emit('confirm');
    },

    /**
     * Get the status variant based on status code
     * @param {String|Number} status - The status code
     * @param {String} type - The approval type ('local' or 'regional')
     * @returns {String} Status variant
     */
    getStatusVariant(status, type = 'local') {
      // Override for regional mapping to ensure correct labels/colors
      // Matches logic in List.vue resolveApprovalStatus and RUApprovalDialog
      if (type === 'regional') {
        const code = Number(status);
        if (code === 0 || code === 1) return STATUS_VARIANTS.REGIONAL_PENDING;
        if (code === 2) return STATUS_VARIANTS.APPROVED;
        if (code === 3) return STATUS_VARIANTS.DISABLED;
      }
      
      // Use the centralized helper for local or fallback
      return getStatusVariantByCode(status) || STATUS_VARIANTS.UNKNOWN;
    },
  }
};
</script>

<style scoped>
/**
 * Styles for the email reminder dialog
 * Uses noTransform class to preserve text casing
 */
.noTransform {
  text-transform: none;
}

/* Table wrapper */
.eligible-ru-table-wrapper {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
}

/* Custom table styling for lb-datatablenew */
.email-reminder-table >>> .data-table-new__table-wrapper {
  max-height: 400px;
  overflow-y: auto;
}
</style>